import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldAlert, Check, Trash2, FileText, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatName, fmtDate } from "@/lib/format";

export default function DuplicatesDialog({ open, onOpenChange, onResolved }) {
  const [groups, setGroups] = useState([]);
  const [keep, setKeep] = useState({}); // groupKey -> client_id to keep
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/clients/duplicates/scan");
      setGroups(res.data.groups || []);
      // Default: keep the one with most policies+documents
      const defaultKeep = {};
      res.data.groups.forEach(g => {
        const sorted = [...g.clients].sort((a, b) =>
          (b.policy_count + b.document_count) - (a.policy_count + a.document_count)
        );
        defaultKeep[g.key] = sorted[0]?.id;
      });
      setKeep(defaultKeep);
    } catch (err) {
      toast.error("Fehler scansione");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const resolveGroup = async (group) => {
    const keepId = keep[group.key];
    if (!keepId) { toast.error("Welchen behalten — auswählen"); return; }
    const deleteIds = group.clients.filter(c => c.id !== keepId).map(c => c.id);
    if (!window.confirm(`NUR behalten ${group.clients.find(c => c.id === keepId)?.first_name} ${group.clients.find(c => c.id === keepId)?.last_name}?\n\nWerden uniti ${deleteIds.length} duplicati (Policen e Dokumente spostati sul Kunde mantenuto).`)) return;
    setBusy(true);
    try {
      await api.post("/clients/duplicates/resolve", { keep_id: keepId, delete_ids: deleteIds });
      toast.success(`Behoben: ${deleteIds.length} duplicati uniti`);
      await load();
      onResolved && onResolved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0">
        <DialogHeader className="px-6 py-4 border-b border-border">
          <div className="label-uppercase text-prime flex items-center gap-1">
            <ShieldAlert className="w-3.5 h-3.5" /> Duplikate
          </div>
          <DialogTitle className="font-display text-2xl tracking-tight">Trova e unisci duplicati</DialogTitle>
          <DialogDescription>
            Kunden con stesso nome o stesso numero Dokument. Welchen behalten — auswählen — i duplicati vengono uniti (Policen e Dokumente spostati sul Kunde scelto).
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 max-h-[70vh] overflow-auto space-y-4">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">
              <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />
              Scansione in corso…
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-12">
              <ShieldCheck className="w-12 h-12 text-green-600 mx-auto mb-3" strokeWidth={1.3} />
              <div className="font-display font-bold text-xl">Kein duplicato</div>
              <p className="text-sm text-muted-foreground mt-1">Il tuo elenco Kunden è pulito.</p>
            </div>
          ) : groups.map((g) => (
            <div key={g.key} className="prime-card p-4" data-testid={`dup-group-${g.key.replace(/\s+/g,'-')}`}>
              <div className="flex items-center justify-between gap-2 mb-3">
                <div>
                  <div className="font-display font-bold text-lg capitalize">{g.key}</div>
                  <div className="label-uppercase text-muted-foreground">
                    {g.clients.length} Duplikate · Übereinstimmung auf {g.match_on === "name" ? "Name" : "Dok.-Nr."}
                  </div>
                </div>
                <Button
                  className="btn-prime gap-1"
                  disabled={busy || !keep[g.key]}
                  onClick={() => resolveGroup(g)}
                  data-testid={`resolve-${g.key.replace(/\s+/g,'-')}`}
                >
                  <Check className="w-4 h-4" /> Gruppe beheben
                </Button>
              </div>
              <div className="space-y-2">
                {g.clients.map(c => {
                  const isKept = keep[g.key] === c.id;
                  return (
                    <label
                      key={c.id}
                      className={`flex items-start gap-3 p-3 border rounded cursor-pointer transition-colors ${
                        isKept ? "border-prime bg-prime/5" : "border-border hover:bg-secondary"
                      }`}
                      data-testid={`dup-client-${c.id}`}
                    >
                      <input
                        type="radio"
                        name={`keep-${g.key}`}
                        value={c.id}
                        checked={isKept}
                        onChange={() => setKeep(k => ({ ...k, [g.key]: c.id }))}
                        className="mt-1 accent-[#DC1F2C]"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{formatName(c.first_name)} {formatName(c.last_name)}</span>
                          {isKept && (
                            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-prime text-white rounded">
                              VERFOLGE
                            </span>
                          )}
                          {!isKept && (
                            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-secondary text-muted-foreground rounded inline-flex items-center gap-1">
                              <Trash2 className="w-2.5 h-2.5" /> unisci
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground font-mono mt-1 space-y-0.5">
                          {c.email && <div>{c.email}</div>}
                          <div>
                            Doc: {c.document_number || "—"} · Bewilligung: {c.permit_type || "—"} · {fmtDate(c.date_of_birth)}
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="inline-flex items-center gap-1">
                              <ShieldCheck className="w-3 h-3" /> {c.policy_count || 0} Policen
                            </span>
                            <span className="inline-flex items-center gap-1">
                              <FileText className="w-3 h-3" /> {c.document_count || 0} doc
                            </span>
                            <span>· creato {new Date(c.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="px-6 py-3 border-t border-border bg-secondary/40">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Schliessen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
